// Trigger GTM send event for campaign forms (for now)
const analytics = {
  _storageId: 'stageNtFormSubmit',

  /**
   * Send form analytics to gtm.
   *
   * @param {Object} data Event properties.
   * @returns {void}
   */
  sendToGTM(data) {
    window.dataLayer = window.dataLayer || [];

    console.log(`form event ={
      event: ${data.eventType},
      form_campaign: ${data.eventName}
    }`)

    window.dataLayer.push({
      event: data.eventType,
      form_campaign: data.eventName
    });
  },

  /**
   * Queue stage form submit event to be resolved on following page load.
   *
   * @param {String} eventType Form gtm event type.
   * @param {String} eventName Form gtm event name.
   * @returns {Boolean} True if event was queued, false if not.
   */
  queueStageFormSubmitEvent(eventType, eventName) {
    try {
      window.localStorage.setItem(this._storageId, JSON.stringify({
        eventType: eventType,
        eventName: eventName,
        pageUrl: window.location.pathname,
        time: Date.now(),
      }));
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * Resolves queued stage form submit event on page load.
   * @returns {Boolean} True if event was resolved, false if not.
   */
  resolveStageFormSubmitEvent() {
    try {
      const event = JSON.parse(window.localStorage.getItem(this._storageId)) || null;
      if (event) {
        if (
          window.location.pathname.indexOf(event.pageUrl) === 0 && // Current page must be either originating form page or direct child (thank you page)
              event.time + 10000 > Date.now() && // Queued event must not be expired (10 seconds)
              !document.querySelector(`[data-gtm-event-name="${event.eventName}"]`) // Originating form must not exist on page (should be thank you message instead)
        ) {
          this.sendToGTM(event);
        }
        window.localStorage.removeItem(this._storageId);
      }
      return true;
    } catch (e) {
      return false;
    }
  }
};


/**
 * Provides GTM event sending for stage forms
 */
class FormAnalytics {

  #form = null;
  #submitting = false;

  /**
   * Create new form.
   *
   * @param {HTMLFormElement} form Form element.
   * @param {object} options Form options.
   */
  constructor(form) {
    this.#form = form;

    this.gtmEventType = form.querySelector('[data-gtm-event-type]')?.dataset?.gtmEventType
    this.gtmEventName = form.querySelector('[data-gtm-event-name]')?.dataset?.gtmEventName

    if (this.gtmEventName && this.gtmEventType) {
      console.log(`FormAnalytics init, data: event: ${this.gtmEventType}, form_campaign: ${this.gtmEventName}`)

      this.#form.addEventListener('submit', (e) => {
        if (e.defaultPrevented) {
          return;
        }

        e.preventDefault();
        this.submit();
      });
    }

  }
  /**
   * Validates and submits form.
   */
  submit() {
    if (this.#submitting) {
      return;
    }

    this.#submitting = true;

    if (this.gtmEventType && this.gtmEventName) {
      analytics.queueStageFormSubmitEvent(this.gtmEventType, this.gtmEventName);
    }
    this.#form.submit();
  }
}

/**
 * This is used for any Stage form element that has a hidden input named 'submit_event_name', not just campaign forms.
 * It delays the submit until the GTM event is sent and resubmits.
 * This is for forms without other handlers. If there are other handlers, they should be used instead.
 *
 * Usage:

 <form class="form--stage" method="post" action="/submit">
   <input type="hidden" name="submit_event_name" value="contact_form_submit">
   ...
   <button type="submit">Submit</button>
 </form>
 */
class FormAnalyticsV2 {
  #submitting = false;
  #eventName;

  constructor(form) {
    if(form) {

      const eventName = form.querySelector('input[name="submit_event_name"]')?.value;

      if(eventName) {
        this.#eventName = eventName;
        form.addEventListener('submit', this.onFormSubmit.bind(this));
      }
    }
  }

  onFormSubmit(event: Event) {
    if (this.#submitting) {
      return;
    }

    this.#submitting = true;

    event.preventDefault();
    const form = event.target;

    // Push your event to the dataLayer
    this.sendToGTM({
      event: this.#eventName,
    });

    // Slight delay to allow GTM or analytics tags to fire
    setTimeout(() => {
      form.submit(); // Proceed with actual submission
    }, 300); // ~300ms is usually enough
  }

  sendToGTM(eventObj) {
    const isAdmin = document.body.classList.contains('view-admin-editor');

    window.dataLayer = window.dataLayer || [];

    if(!isAdmin) {
      window.dataLayer.push(eventObj);
    } else {
      console.log(`form-analytics-v2: ${JSON.stringify(eventObj, null, 2)}`);
    }
  }
}

function initFormAnalytics() {
  analytics.resolveStageFormSubmitEvent();

  // Common form, newsletter, myvianor profile form
  document.querySelectorAll('form.form--stage').forEach(form => {
    // Check if form has submit_event_name input
    const hasSubmitEventName = form.querySelector('input[name="submit_event_name"]');
    if (hasSubmitEventName) {
      new FormAnalyticsV2(form);
    } else {
      new FormAnalytics(form);
    }
  });
}

export default FormAnalytics;
export { initFormAnalytics, analytics };
